import type { ScreenBase } from "@frui.ts/screens/dist";
import { Router } from "@frui.ts/screens/dist";
import { Modules } from "config";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import SecurityService from "services/securityService";
import ListViewModel from "viewModels/listViewModel";
import NotificationService from "services/notificationService";
import AccountingDocumentsInventoryResponse from "entities/accountingDocumentsInventoryResponse";
import SearchAccountingDocumentsInventoriesRequest from "entities/searchAccountingDocumentsInventoriesRequest";
import { action, computed, observable, reaction } from "mobx";
import MonthOptionsService from "services/monthOptionsService";
import Month from "entities/month";
import MonthOfYearRangeResponse from "entities/monthOfYearRangeResponse";
import AccountingDocumentsInventoryStatus from "entities/accountingDocumentsInventoryStatus";
import AccountingDocumentsInventoryCheckResult from "entities/accountingDocumentsInventoryCheckResult";
import DocumentsInventoryCreateViewModel from "./documentsInventoryCreateViewModel";
import DocumentsInventoryDetailViewModel from "./documentsInventoryDetailViewModel";
import AccountingDocumentsInventoryRepository from "repositories/accountingDocumentsInventoryRepository";
import AccountingDocumentsInventoriesPermittedActionsResponse from "entities/accountingDocumentsInventoriesPermittedActionsResponse";
import { attachManualValidator, addError, removeError } from "@frui.ts/validation";
import { getValidationMessage } from "@frui.ts/validation";
import camelCase from "lodash/camelCase";
import ConfirmationService from "services/confirmationService";
import download from "downloadjs";

const navigationName = "documents-inventory";

export enum DocumentsInventoryAction {
  CHANGE = "Change",
  CHECK = "Check",
  CHECK_RESULT_DOWNLOAD = "CheckResultDownload",
  CLOSURE = "Closure",
  DELETION = "Deletion",
  DETAIL_ACCESS = "DetailAccess",
}

@Router.registerRoute({
  route: navigationName,
  name: Router.Self,
  children: [DocumentsInventoryCreateViewModel, DocumentsInventoryDetailViewModel],
})
export default class DocumentsInventoryViewModel extends ListViewModel<
  AccountingDocumentsInventoryResponse,
  ScreenBase,
  DocumentsInventoryCreateViewModel,
  SearchAccountingDocumentsInventoriesRequest
> {
  readonly modelName = "documentsInventory";
  @observable toMonth?: Date;
  @observable fromMonth?: Date;
  @observable permittedActions?: AccountingDocumentsInventoriesPermittedActionsResponse;
  protected abortController?: AbortController;
  private isFirstActivation = true;
  private pollingInterval?: NodeJS.Timeout;

  constructor(
    protected createViewModelFactory: ReturnType<typeof DocumentsInventoryCreateViewModel.Factory>,
    protected detailViewModel: ReturnType<typeof DocumentsInventoryDetailViewModel.Factory>,
    protected localizationService: LocalizationService,
    protected monthOptionsService: MonthOptionsService,
    protected repository: AccountingDocumentsInventoryRepository,
    protected eventBus: EventBus,
    protected securityService: SecurityService,
    protected notificationService: NotificationService,
    protected confirmationService: ConfirmationService
  ) {
    super();
    this.navigationName = navigationName;
  }

  onInitialize(): Promise<any> | void {
    attachManualValidator(this, true);

    this.unsubscriptions.push(
      reaction(
        () => [this.toMonth],
        () => {
          this.setToMonth();
          this.validateMonthRange();
        }
      ),
      reaction(
        () => [this.fromMonth],
        () => {
          this.setFromMonth();
          this.validateMonthRange();
        }
      )
    );
    return super.onInitialize();
  }

  protected onActivate(): Promise<void> {
    // Skip loading on first activation as onInitialize already triggers resetFilterAndLoad
    if (this.isFirstActivation) {
      this.isFirstActivation = false;
    } else {
      this.loadData();
    }
    return super.onActivate();
  }

  protected createFilter(): SearchAccountingDocumentsInventoriesRequest {
    return new SearchAccountingDocumentsInventoriesRequest();
  }

  protected async fetchData() {
    const response = await this.repository.search(this.appliedFilter, this.pagingFilter);
    this.permittedActions = response.permittedActions;

    return {
      items: response.items,
      metadata: response.metadata,
    };
  }

  @computed
  get months() {
    return this.monthOptionsService.startOfMonthOptions.map(option => ({
      ...option,
      label: option.label.replace(/^(\d{4}) - (\d{2})$/, "$2/$1"),
    }));
  }

  @computed
  get toSelectedMonth() {
    if (this.toMonth) {
      return new Month(this.toMonth);
    }
    return undefined;
  }
  @computed
  get fromSelectedMonth() {
    if (this.fromMonth) {
      return new Month(this.fromMonth);
    }
    return undefined;
  }

  @computed
  get hasFilterChanges() {
    return !!(this.fromMonth ?? this.toMonth);
  }

  @computed
  get isFilterValid() {
    return !this.fromMonthError && !this.toMonthError && this.hasFilterChanges;
  }

  @computed
  get navigationParams() {
    return {
      offset: this.pagingFilter.offset,
    };
  }

  @computed
  get allowDelete() {
    return false;
  }

  @computed
  get allowWrite() {
    return true;
  }

  @computed
  get allowRead() {
    return false;
  }

  @computed
  get fromMonthError() {
    return getValidationMessage(this, "fromMonth");
  }

  @computed
  get toMonthError() {
    return getValidationMessage(this, "toMonth");
  }

  @action.bound
  actions(item: AccountingDocumentsInventoryResponse) {
    return Object.values(DocumentsInventoryAction).filter(action => this.allowToAction(action, item));
  }

  @action.bound
  allowToAction(action: DocumentsInventoryAction, item: AccountingDocumentsInventoryResponse) {
    switch (action) {
      case DocumentsInventoryAction.CHANGE:
        return this.allowToChange(item);
      case DocumentsInventoryAction.CHECK:
        return this.allowToCheck(item);
      case DocumentsInventoryAction.CHECK_RESULT_DOWNLOAD:
        return this.allowToCheckResultDownload(item);
      case DocumentsInventoryAction.CLOSURE:
        return this.allowToClosure(item);
      case DocumentsInventoryAction.DELETION:
        return this.allowToDeletion(item);
      case DocumentsInventoryAction.DETAIL_ACCESS:
        return this.allowToDetailAccess(item);
      default:
        return false;
    }
  }

  @action.bound
  allowToChange(item: AccountingDocumentsInventoryResponse) {
    return item.permittedActions.inventoryChange;
  }

  @action.bound
  allowToCheck(item: AccountingDocumentsInventoryResponse) {
    return item.permittedActions.inventoryCheck;
  }
  @action.bound
  allowToCheckResultDownload(item: AccountingDocumentsInventoryResponse) {
    return item.permittedActions.inventoryCheckResultDownload;
  }
  @action.bound
  allowToClosure(item: AccountingDocumentsInventoryResponse) {
    return item.permittedActions.inventoryClosure;
  }
  @action.bound
  allowToDeletion(item: AccountingDocumentsInventoryResponse) {
    return item.permittedActions.inventoryDeletion;
  }
  @action.bound
  allowToDetailAccess(item: AccountingDocumentsInventoryResponse) {
    return item.permittedActions.inventoryDetailAccess;
  }

  @action.bound
  onChangeClick(item: AccountingDocumentsInventoryResponse) {
    const createViewModelFactory = this.createViewModelFactory;
    if (createViewModelFactory) {
      this.tryActivateChild(
        createViewModelFactory(item.accountingPeriodMonthRange.from, item.accountingPeriodMonthRange.to, item.id)
      );
    }
  }

  @action.bound
  async onCheckClick(item: AccountingDocumentsInventoryResponse) {
    try {
      await this.repository.checks(item.id);
      await this.loadData();
    } catch (error) {
      // Error handling is done by repository/interceptor
    }
  }

  @action.bound
  async onCheckResultDownloadClick(item: AccountingDocumentsInventoryResponse) {
    try {
      const fileResponse = await this.repository.checkResultFile(item.id);
      const fileName = `Výsledek kontroly inventury ${item.accountingPeriodMonthRange.from} - ${item.accountingPeriodMonthRange.to}.xlsx`;
      download(fileResponse.data, fileName, "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
    } catch (error) {
      // Error handling is done by repository/interceptor
    }
  }

  @action.bound
  async onClosureClick(item: AccountingDocumentsInventoryResponse) {
    try {
      await this.repository.closures(item.id);
      await this.loadData();
    } catch (error) {
      // Error handling is done by repository/interceptor
    }
  }

  @action.bound
  async onDeletionClick(item: AccountingDocumentsInventoryResponse) {
    const confirmed = await this.confirmationService.deleteConfirmation();

    if (confirmed) {
      try {
        await this.repository.delete(item.id);
        await this.loadData();
      } catch (error) {
        // Error handling is done by repository/interceptor
      }
    }
  }

  @action.bound
  onDetailAccessClick(item: AccountingDocumentsInventoryResponse) {
    const detailViewModel = this.detailViewModel;
    if (detailViewModel) {
      this.tryActivateChild(detailViewModel(item.id));
    }
  }

  disabledActionTooltip(action: DocumentsInventoryAction, inventory: AccountingDocumentsInventoryResponse) {
    let act: { rejectionReason?: string; isAllowed?: boolean } | undefined;
    switch (action) {
      case DocumentsInventoryAction.CHANGE:
        act = inventory.permittedActions.inventoryChange;
        break;
      case DocumentsInventoryAction.CHECK:
        act = inventory.permittedActions.inventoryCheck;
        break;
      case DocumentsInventoryAction.CHECK_RESULT_DOWNLOAD:
        act = inventory.permittedActions.inventoryCheckResultDownload;
        break;
      case DocumentsInventoryAction.CLOSURE:
        act = inventory.permittedActions.inventoryClosure;
        break;
      case DocumentsInventoryAction.DELETION:
        act = inventory.permittedActions.inventoryDeletion;
        break;
      case DocumentsInventoryAction.DETAIL_ACCESS:
        act = inventory.permittedActions.inventoryDetailAccess;
        break;
      default:
        act = undefined;
        break;
    }
    return act?.rejectionReason ? this.translate(`actions.${camelCase(action)}.${act.rejectionReason}`) : undefined;
  }

  @action.bound
  private setFromMonth() {
    if (!this.filter.accountingPeriodMonthRange) {
      this.filter.accountingPeriodMonthRange = new MonthOfYearRangeResponse();
    }
    this.filter.accountingPeriodMonthRange.from = this.fromSelectedMonth
      ? `${this.fromSelectedMonth.year}-${this.fromSelectedMonth.month.toString().padStart(2, "0")}`
      : undefined;
  }

  @action.bound
  private setToMonth() {
    if (!this.filter.accountingPeriodMonthRange) {
      this.filter.accountingPeriodMonthRange = new MonthOfYearRangeResponse();
    }
    this.filter.accountingPeriodMonthRange.to = this.toSelectedMonth
      ? `${this.toSelectedMonth.year}-${this.toSelectedMonth.month.toString().padStart(2, "0")}`
      : undefined;
  }

  @action.bound
  validateMonthRange() {
    removeError(this, "fromMonth");
    removeError(this, "toMonth");

    if (this.fromMonth && this.toMonth && this.toMonth < this.fromMonth) {
      addError(this, "toMonth", this.localizationService.translate("validators.customMessages.afterSinceDate"));
    }
  }

  @action.bound
  resetFilterAndLoad() {
    this.fromMonth = undefined;
    this.toMonth = undefined;

    const result = super.resetFilterAndLoad();

    // Ensure the range object properties are cleared after parent reset
    if (this.filter.accountingPeriodMonthRange) {
      this.filter.accountingPeriodMonthRange.from = undefined;
      this.filter.accountingPeriodMonthRange.to = undefined;
    }

    return result;
  }

  @action.bound
  onAddClick() {
    const createViewModelFactory = this.createViewModelFactory;
    if (createViewModelFactory && this.allowWrite) {
      return () =>
        this.tryActivateChild(
          createViewModelFactory(
            this.permittedActions?.inventoryCreation.requiredAccountingPeriodFrom,
            this.permittedActions?.inventoryCreation.latestAccountingPeriodTo
          )
        );
    } else {
      return undefined;
    }
  }

  @action.bound
  async findNavigationChild(navigationName?: string) {
    if (navigationName === "new") {
      return this.createViewModelFactory
        ? this.createViewModelFactory(
            this.permittedActions?.inventoryCreation.requiredAccountingPeriodFrom,
            this.permittedActions?.inventoryCreation.latestAccountingPeriodTo
          )
        : undefined;
    } else if (navigationName) {
      const detail = await this.repository.get(navigationName);
      return this.createViewModelFactory
        ? this.createViewModelFactory(
            detail.accountingPeriodMonthRange.from,
            detail.accountingPeriodMonthRange.to,
            navigationName
          )
        : undefined;
    }

    return Promise.resolve(undefined);
  }

  @action.bound
  getStatusTextClass(status: AccountingDocumentsInventoryStatus, checkResult?: AccountingDocumentsInventoryCheckResult): string {
    const textMutedClass = "text-muted";

    // Speciální případ: uzavřená + OK = šedá
    if (status === AccountingDocumentsInventoryStatus.Closed && checkResult === AccountingDocumentsInventoryCheckResult.Ok) {
      return textMutedClass;
    }

    switch (status) {
      case AccountingDocumentsInventoryStatus.ToBeChecked:
        return "text-dark-blue";
      case AccountingDocumentsInventoryStatus.Checked:
        return "text-success";
      case AccountingDocumentsInventoryStatus.Created:
        return "text-warning";
      case AccountingDocumentsInventoryStatus.CheckFailed:
        return "text-danger";
      case AccountingDocumentsInventoryStatus.Closed:
        return textMutedClass;
      default:
        return textMutedClass;
    }
  }

  @action.bound
  getCheckResultTextClass(
    checkResult: AccountingDocumentsInventoryCheckResult,
    status?: AccountingDocumentsInventoryStatus
  ): string {
    const textMutedClass = "text-muted";

    // Speciální případ: uzavřená + OK = šedá
    if (status === AccountingDocumentsInventoryStatus.Closed && checkResult === AccountingDocumentsInventoryCheckResult.Ok) {
      return textMutedClass;
    }

    switch (checkResult) {
      case AccountingDocumentsInventoryCheckResult.DifferencesFound:
        return "text-danger";
      case AccountingDocumentsInventoryCheckResult.Ok:
        return "text-success";
      default:
        return textMutedClass;
    }
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `inventory.documentsInventory.${code}`, size);
  }

  translateStatus(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `inventory.documentsInventory.statusTypes.${code}`, size);
  }

  translateCheckResult(code: string, size?: number) {
    return this.localizationService.translateModule(
      Modules.finance,
      `inventory.documentsInventory.checkResultTypes.${code}`,
      size
    );
  }

  translateGeneral(code: string, size?: number) {
    return this.localizationService.translateGeneral(code, size);
  }

  translateAttribute(code: string, size?: number) {
    return this.localizationService.translateAttribute("documentsInventory", code, size);
  }
}
